import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Edge-anchored panel for secondary detail — audit trails, row drilldowns,
 * filter stacks on small screens.
 *
 * Before this existed every drawer in the app was hand-rolled (three different
 * bespoke implementations plus the mobile nav repurposing `Dialog`), so none of
 * them agreed on width, animation, focus handling or how to close.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  side = 'right',
  width = 'md',
  footer,
  children,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  description?: ReactNode;
  side?: 'right' | 'left';
  width?: 'sm' | 'md' | 'lg';
  footer?: ReactNode;
  children: ReactNode;
}) {
  const widthClass = width === 'sm' ? 'w-80' : width === 'lg' ? 'w-[38rem]' : 'w-[28rem]';
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'duration-200 ease-out',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-y-0 z-[1010] flex h-dvh max-h-dvh max-w-[92vw] flex-col border-border bg-surface shadow-xl',
            widthClass,
            side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            side === 'right'
              ? 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
              : 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
            'duration-240 ease-out',
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3">
            <div className="min-w-0">
              <DialogPrimitive.Title className="font-display text-sm font-semibold tracking-tight">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-0.5 text-xs text-muted-foreground">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close
              aria-label="Close panel"
              className="press shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground focus-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">{children}</div>

          {footer ? (
            <div className="shrink-0 border-t border-border px-4 py-3">{footer}</div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
